import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  formatDemoCountriesSchedulePreview,
  formatEnteredSchedulePreview,
  getDateAndTimeInTimezone,
  getViewerTimezone,
} from '../../utils/dateTimeUtils';
import {
  getTimezoneAbbreviation,
  getTimezoneOptions,
  normalizeTimezoneForFormSelect,
  normalizeTimezoneForStorage,
} from '../../utils/timezoneUtils';
import { useAuth } from '../../contexts/AuthContext';
import { demosService } from '../../services/demos';
import { teachersService } from '../../services/teachers';
import TimeSelector from '../../components/TimeSelector';
import { DemoCountryFullLines } from '../../components/Demos/DemoCountrySchedule';
import { hasMeetingUrl, normalizeMeetingUrlInput } from '../../utils/meetingUrl';
import DemoScheduleExceptionPanel from '../../components/Demos/DemoScheduleExceptionPanel';
import {
  checkDemoTimeAgainstTeacherAvailability,
  buildScheduleExceptionNote,
  formatTimeAMPM,
} from '../../utils/demoTeacherAvailabilityCheck';
import { getCountries } from '../../data/locationData';
import { formatDemoInstructorLabel } from '../../utils/demoInstructorLabel';
import toast from 'react-hot-toast';

const CHANNEL_OPTIONS = [
  {
    value: 'crm_only',
    label: 'Only for our team',
    hint: 'Staff book children from the CRM. Not shown on the website.',
  },
  {
    value: 'landing_only',
    label: 'On the website',
    hint: 'Parents pick this slot on the landing page.',
  },
  {
    value: 'both',
    label: 'Team and website',
    hint: 'Shown in CRM and on the website.',
  },
];

function FormSection({ title, hint, children }) {
  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {title}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      ) : null}
      {children}
    </Grid>
  );
}

function countriesKey(list) {
  return [...(list || [])].map(String).sort().join('|');
}

function normalizeTimeKey(time) {
  const s = String(time || '').trim();
  if (!s) return '';
  if (s.length >= 8) return s.slice(0, 8);
  const hm = s.slice(0, 5);
  return hm.length === 5 ? `${hm}:00` : s;
}

const DemoEditForm = ({ demo, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const defaultTimezone = normalizeTimezoneForFormSelect(
    demo?.scheduled_timezone || user?.timezone || 'UTC'
  );

  const [formData, setFormData] = useState({
    name: '',
    type: 'demo',
    countries: [],
    channel: 'crm_only',
    capacity: 12,
    assigned_sales_user_id: '',
    scheduled_date: null,
    scheduled_time: '',
    duration_minutes: 30,
    scheduled_timezone: defaultTimezone,
    teacher_id: '',
    notes: '',
    meeting_url: '',
  });
  const [settingsBaseline, setSettingsBaseline] = useState(null);
  const [scheduleBaseline, setScheduleBaseline] = useState(null);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [availableTimeRanges, setAvailableTimeRanges] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [availabilityMeta, setAvailabilityMeta] = useState(null);
  const [scheduleExceptionReason, setScheduleExceptionReason] = useState(null);
  const [allowScheduleException, setAllowScheduleException] = useState(false);

  const enteredTimezone = formData.scheduled_timezone || defaultTimezone;
  const enteredAbbr = getTimezoneAbbreviation(enteredTimezone);

  const fetchTeachers = useCallback(async (sessionType = 'demo') => {
    try {
      const response = await teachersService.getTeachersForSessionType(sessionType);
      const data = response?.data ?? response;
      setTeachers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setTeachers([]);
    }
  }, []);

  const settingsSnapshot = useCallback((data) => ({
    name: (data.name || '').trim(),
    type: data.type || 'demo',
    countries: countriesKey(data.countries),
    channel: data.channel || 'crm_only',
    capacity: Number(data.capacity) || 12,
    assigned_sales_user_id: data.assigned_sales_user_id || '',
    teacher_id: data.teacher_id || '',
    meeting_url: normalizeMeetingUrlInput(data.meeting_url) || '',
  }), []);

  const scheduleSnapshot = useCallback((data) => ({
    date: data.scheduled_date ? dayjs(data.scheduled_date).format('YYYY-MM-DD') : '',
    time: normalizeTimeKey(data.scheduled_time),
    timezone: normalizeTimezoneForStorage(data.scheduled_timezone || defaultTimezone) || 'UTC',
    duration: Number(data.duration_minutes) || 30,
  }), [defaultTimezone]);

  useEffect(() => {
    if (!demo) return;
    let currentDate;
    let timeWithoutSeconds = demo.scheduled_time ? String(demo.scheduled_time).substring(0, 5) : '';
    const storedTz = getViewerTimezone(demo.scheduled_timezone || user?.timezone);
    if (demo.scheduled_at_iso && storedTz) {
      const inStoredTZ = getDateAndTimeInTimezone(demo.scheduled_at_iso, storedTz);
      if (inStoredTZ) {
        currentDate = dayjs(inStoredTZ.dateStr);
        timeWithoutSeconds = inStoredTZ.timeStr;
      }
    }
    if (!currentDate) {
      try {
        if (demo.scheduled_date && demo.scheduled_time) {
          currentDate = typeof demo.scheduled_date === 'string' && demo.scheduled_date.includes('T')
            ? dayjs(demo.scheduled_date)
            : dayjs(`${demo.scheduled_date}T${demo.scheduled_time}`);
        } else {
          currentDate = dayjs();
        }
      } catch (err) {
        console.error('Error parsing demo date:', err);
        currentDate = dayjs();
      }
    }
    const next = {
      name: demo.name || '',
      type: demo.type || 'demo',
      countries: Array.isArray(demo.countries) ? demo.countries : [],
      channel: demo.channel || 'crm_only',
      capacity: demo.capacity || 12,
      assigned_sales_user_id: demo.assigned_sales_user_id || '',
      scheduled_date: currentDate,
      scheduled_time: timeWithoutSeconds,
      duration_minutes: demo.duration_minutes || 30,
      scheduled_timezone: normalizeTimezoneForFormSelect(demo.scheduled_timezone || user?.timezone || 'UTC'),
      teacher_id: demo.teacher_id || '',
      notes: '',
      meeting_url: demo.meeting_url || '',
    };
    setFormData(next);
    setSettingsBaseline(settingsSnapshot(next));
    setScheduleBaseline(scheduleSnapshot(next));
    setError('');
    setScheduleExceptionReason(null);
    setAllowScheduleException(false);
    fetchTeachers(next.type || 'demo');
    demosService.getSalespeople()
      .then((res) => {
        const list = res?.data ?? res ?? [];
        setSalespeople(Array.isArray(list) ? list : []);
      })
      .catch(() => setSalespeople([]));
    // Re-init only when opening a different demo so Save settings does not wipe an unsaved time change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo?.id]);

  const teacherOptions = useMemo(() => {
    const list = Array.isArray(teachers) ? [...teachers] : [];
    if (demo?.teacher_id && !list.some((t) => String(t.id) === String(demo.teacher_id))) {
      list.unshift({
        id: demo.teacher_id,
        first_name: demo.teacher_first_name || 'Current',
        last_name: demo.teacher_last_name || 'teacher',
        role: demo.teacher_role,
      });
    }
    return list;
  }, [teachers, demo]);

  const salesOptions = useMemo(() => {
    const list = Array.isArray(salespeople) ? [...salespeople] : [];
    if (
      demo?.assigned_sales_user_id &&
      !list.some((s) => String(s.id) === String(demo.assigned_sales_user_id))
    ) {
      list.unshift({
        id: demo.assigned_sales_user_id,
        first_name: demo.assigned_sales_first_name || 'Current',
        last_name: demo.assigned_sales_last_name || 'salesperson',
      });
    }
    return list;
  }, [salespeople, demo]);

  const fetchAvailableTimeRanges = useCallback(async (teacherId, date, timezone) => {
    if (!teacherId || !date) return;
    try {
      setLoadingSlots(true);
      const formattedDate = dayjs(date).format('YYYY-MM-DD');
      const response = await demosService.getAvailableSlots(
        teacherId,
        formattedDate,
        timezone,
        { exclude_demo_id: demo?.id }
      );
      setAvailableTimeRanges(response.data.available_time_ranges || []);
      setAvailabilityMeta({
        teacher_availability: response.data.teacher_availability,
        booked_batches: response.data.booked_batches || [],
        booked_demos: response.data.booked_demos || [],
        skip_work_hours: !!response.data.skip_work_hours,
      });
      setTeacherInfo({
        name: response.data.teacher_name,
        timezone: response.data.teacher_timezone,
        studentTimezone: response.data.student_timezone,
        availability: response.data.teacher_availability,
      });
    } catch (err) {
      console.error('Error fetching available time ranges:', err);
      setAvailableTimeRanges([]);
      setTeacherInfo(null);
      setAvailabilityMeta({
        teacher_availability: null,
        booked_batches: [],
        booked_demos: [],
      });
    } finally {
      setLoadingSlots(false);
    }
  }, [demo?.id]);

  useEffect(() => {
    if (!formData.teacher_id || !formData.scheduled_date) {
      setAvailableTimeRanges([]);
      setAvailabilityMeta(null);
      setTeacherInfo(null);
      setScheduleExceptionReason(null);
      setAllowScheduleException(false);
      return;
    }
    fetchAvailableTimeRanges(formData.teacher_id, formData.scheduled_date, enteredTimezone);
  }, [formData.teacher_id, formData.scheduled_date, enteredTimezone, fetchAvailableTimeRanges]);

  const dayLabel = formData.scheduled_date ? dayjs(formData.scheduled_date).format('dddd') : 'this day';

  const recomputeScheduleException = useCallback(
    (time, durationMin) => {
      const startTime = time ?? formData.scheduled_time;
      const duration = durationMin ?? formData.duration_minutes;
      if (!startTime || !formData.teacher_id || !formData.scheduled_date) {
        setScheduleExceptionReason(null);
        setAllowScheduleException(false);
        return;
      }
      if (loadingSlots || availabilityMeta == null) {
        setScheduleExceptionReason(null);
        return;
      }
      const result = checkDemoTimeAgainstTeacherAvailability({
        startTime,
        durationMinutes: duration,
        availableTimeRanges,
        teacherAvailability: availabilityMeta?.teacher_availability,
        bookedBatches: availabilityMeta?.booked_batches || [],
        bookedDemos: availabilityMeta?.booked_demos || [],
        dayLabel,
        skipWorkHours: !!availabilityMeta?.skip_work_hours,
      });
      setScheduleExceptionReason(result.exceptionReason);
      if (result.ok) {
        setAllowScheduleException(false);
      } else if (!(availabilityMeta?.teacher_availability?.slots || []).length) {
        setAllowScheduleException(true);
      } else {
        setAllowScheduleException(false);
      }
    },
    [
      formData.scheduled_time,
      formData.duration_minutes,
      formData.teacher_id,
      formData.scheduled_date,
      availableTimeRanges,
      availabilityMeta,
      loadingSlots,
      dayLabel,
    ]
  );

  useEffect(() => {
    recomputeScheduleException();
  }, [recomputeScheduleException]);

  const handleChange = (field, value) => {
    if (field === 'type') {
      fetchTeachers(value);
      setFormData((prev) => ({ ...prev, type: value, teacher_id: '' }));
      setError('');
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
    if (field === 'duration_minutes') {
      setTimeout(() => recomputeScheduleException(formData.scheduled_time, value), 0);
    }
  };

  const handleTimeChange = (time) => {
    setFormData((prev) => ({ ...prev, scheduled_time: time || '' }));
    setError('');
    if (!time) {
      setScheduleExceptionReason(null);
      setAllowScheduleException(false);
      return;
    }
    recomputeScheduleException(time, formData.duration_minutes);
  };

  const settingsDirty = settingsBaseline
    ? JSON.stringify(settingsSnapshot(formData)) !== JSON.stringify(settingsBaseline)
    : false;
  const scheduleDirty = scheduleBaseline
    ? JSON.stringify(scheduleSnapshot(formData)) !== JSON.stringify(scheduleBaseline)
    : false;

  const landingChannel = formData.channel === 'landing_only' || formData.channel === 'both';
  const meetingOk = hasMeetingUrl(formData.meeting_url);
  const timeFormatOk = (() => {
    if (!formData.scheduled_time) return false;
    const timeStr = formData.scheduled_time.includes(':') && formData.scheduled_time.length >= 8
      ? formData.scheduled_time.slice(0, 8)
      : `${String(formData.scheduled_time).slice(0, 5)}:00`;
    return /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(timeStr);
  })();

  const formattedTime = () => {
    const timeStr = formData.scheduled_time.includes(':') && formData.scheduled_time.length >= 8
      ? formData.scheduled_time.slice(0, 8)
      : `${String(formData.scheduled_time).slice(0, 5)}:00`;
    return timeStr;
  };

  const handleApiError = (submitError, fallback) => {
    console.error('Demo update error:', submitError);
    if (submitError.response?.data?.errors && Array.isArray(submitError.response.data.errors)) {
      setError(submitError.response.data.errors.join(', '));
    } else {
      setError(
        submitError.response?.data?.message ||
          submitError.response?.data?.error ||
          fallback
      );
    }
  };

  const handleSaveSettings = async () => {
    if (!formData.countries?.length) {
      setError('Select which countries this demo is for');
      return;
    }
    if (landingChannel && !formData.assigned_sales_user_id) {
      setError('Pick a salesperson — they will own website bookings for this demo');
      return;
    }
    if (!meetingOk) {
      setError('Demo join link is required');
      return;
    }
    const capacity = Number(formData.capacity) || 12;
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 50) {
      setError('Capacity must be between 1 and 50');
      return;
    }

    try {
      setSaving('settings');
      setError('');
      const response = await demosService.updateDemo(demo.id, {
        name: (formData.name || '').trim() || demo.name,
        type: formData.type,
        countries: formData.countries,
        channel: formData.channel,
        capacity,
        assigned_sales_user_id: formData.assigned_sales_user_id || null,
        teacher_id: formData.teacher_id || null,
        meeting_url: normalizeMeetingUrlInput(formData.meeting_url),
      });
      setSettingsBaseline(settingsSnapshot(formData));
      toast.success('Slot settings saved. Time was not changed.');
      onSuccess(response, { close: false });
    } catch (submitError) {
      handleApiError(submitError, 'Failed to save slot settings');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveNewTime = async () => {
    if (!formData.scheduled_date || !formData.scheduled_time) {
      setError('Pick a date and time');
      return;
    }
    if (!timeFormatOk) {
      setError('Invalid time format');
      return;
    }
    if (formData.teacher_id && scheduleExceptionReason && !allowScheduleException) {
      setError('Confirm the schedule exception to continue, or choose a time within availability.');
      return;
    }
    if (!meetingOk) {
      setError('Demo join link is required');
      return;
    }

    try {
      setSaving('time');
      setError('');
      let notes = formData.notes?.trim() || '';
      if (formData.teacher_id && scheduleExceptionReason && allowScheduleException) {
        notes = buildScheduleExceptionNote(scheduleExceptionReason, notes);
      }
      const response = await demosService.updateDemo(demo.id, {
        scheduled_date: dayjs(formData.scheduled_date).format('YYYY-MM-DD'),
        scheduled_time: formattedTime(),
        scheduled_timezone: normalizeTimezoneForStorage(enteredTimezone) || 'UTC',
        duration_minutes: Number(formData.duration_minutes) || 30,
        ...(notes && { notes }),
      });
      setScheduleBaseline(scheduleSnapshot(formData));
      setFormData((prev) => ({ ...prev, notes: '' }));
      toast.success(response?.autoRescheduled
        ? 'New time saved. Demo marked as rescheduled.'
        : 'Time saved.');
      onSuccess(response, { close: false });
    } catch (submitError) {
      handleApiError(submitError, 'Failed to save new time');
    } finally {
      setSaving(null);
    }
  };

  const countryPreview = useMemo(
    () => formatDemoCountriesSchedulePreview(
      formData.scheduled_date,
      formData.scheduled_time,
      enteredTimezone,
      formData.countries || []
    ),
    [formData.scheduled_date, formData.scheduled_time, enteredTimezone, formData.countries]
  );
  const enteredPreview = useMemo(
    () => formatEnteredSchedulePreview(
      formData.scheduled_date,
      formData.scheduled_time,
      enteredTimezone
    ),
    [formData.scheduled_date, formData.scheduled_time, enteredTimezone]
  );

  const currentTeacherName = demo?.teacher_first_name
    ? `${demo.teacher_first_name} ${demo.teacher_last_name || ''}`.trim()
    : 'Not assigned';
  const busy = Boolean(saving);

  return (
    <Box>
      {error ? (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Current time</Typography>
        <DemoCountryFullLines demo={demo} viewerTimezone={viewerTimezone} />
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {demo?.duration_minutes || 30} min · {currentTeacherName}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Slot settings and session time save separately. Changing the teacher does not mark the demo as rescheduled.
        </Typography>
      </Alert>

      <Grid container spacing={2}>
        <FormSection
          title="1. When is it?"
          hint="Type the time on the clock families use. If the demo is 4pm in New York, choose US Eastern and enter 4:00 — no need to convert to India time. Use Save new time for this section."
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date"
                  value={formData.scheduled_date}
                  onChange={(date) => handleChange('scheduled_date', date)}
                  minDate={dayjs().subtract(10, 'day')}
                  slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="How long (minutes)"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value, 10) || 30)}
                inputProps={{ min: 15, max: 120, step: 15 }}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-edit-timezone-label">This time is on which clock?</InputLabel>
                <Select
                  labelId="demo-edit-timezone-label"
                  value={enteredTimezone}
                  label="This time is on which clock?"
                  onChange={(e) => handleChange('scheduled_timezone', e.target.value)}
                >
                  {getTimezoneOptions().map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TimeSelector
                label="Time"
                value={formData.scheduled_time}
                onChange={handleTimeChange}
                size="small"
                error={!!formData.teacher_id && !!scheduleExceptionReason && !allowScheduleException}
                helperText={
                  formData.teacher_id && scheduleExceptionReason && !allowScheduleException
                    ? 'Outside normal availability — confirm exception below to continue'
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                {!formData.scheduled_date || !formData.scheduled_time ? (
                  <Typography variant="body2">
                    Pick a date, clock, and time. We will show what families in each country will see.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      You entered {countryPreview?.entered || '—'} ({enteredAbbr}).
                    </Typography>
                    {(formData.countries || []).length ? (
                      (countryPreview?.countries || []).map((row) => (
                        <Typography key={row.country} variant="body2" sx={{ mt: 0.75 }}>
                          <strong>{row.country} families see:</strong> {row.line}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.75 }}>
                        In India (IST): {enteredPreview?.ist || '—'}
                      </Typography>
                    )}
                  </>
                )}
              </Alert>
            </Grid>
          </Grid>
        </FormSection>

        <Grid item xs={12}><Divider /></Grid>

        <FormSection
          title="2. Who is this for?"
          hint="Use Save settings for countries, channel, salesperson, capacity, teacher, and join link."
        >
          <FormControl fullWidth size="small" required>
            <InputLabel id="demo-edit-countries-label">Countries</InputLabel>
            <Select
              labelId="demo-edit-countries-label"
              multiple
              value={formData.countries}
              onChange={(e) => handleChange('countries', e.target.value)}
              input={<OutlinedInput label="Countries" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected || []).map((c) => (
                    <Chip key={c} label={c} size="small" />
                  ))}
                </Box>
              )}
            >
              {getCountries().map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
            Where should families see this slot?
          </Typography>
          <RadioGroup
            value={formData.channel}
            onChange={(e) => handleChange('channel', e.target.value)}
          >
            {CHANNEL_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio size="small" />}
                sx={{ alignItems: 'flex-start', ml: 0, mb: 0.5 }}
                label={(
                  <Box sx={{ mt: 0.4 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.hint}</Typography>
                  </Box>
                )}
              />
            ))}
          </RadioGroup>
        </FormSection>

        <Grid item xs={12}><Divider /></Grid>

        <FormSection
          title="3. Who runs it?"
          hint={landingChannel
            ? 'Website bookings are always assigned to the salesperson you pick here. Teacher changes do not reschedule the demo.'
            : 'Teacher and salesperson are optional for team-only demos. Teacher changes do not reschedule the demo.'}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required={landingChannel}>
                <InputLabel>Salesperson</InputLabel>
                <Select
                  value={formData.assigned_sales_user_id}
                  label="Salesperson"
                  onChange={(e) => handleChange('assigned_sales_user_id', e.target.value)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {salesOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}
                      {s.role && s.role !== 'sales' ? ` (${String(s.role).replace(/_/g, ' ')})` : ''}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {landingChannel
                    ? 'Required — new website registrations go to this person'
                    : 'Optional. Changing salesperson does not reassign existing leads'}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Teacher (optional)</InputLabel>
                <Select
                  size="small"
                  value={formData.teacher_id}
                  onChange={(e) => handleChange('teacher_id', e.target.value)}
                  label="Teacher (optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  {teacherOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {formatDemoInstructorLabel(t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="How many children?"
                type="number"
                inputProps={{ min: 1, max: 50 }}
                value={formData.capacity}
                onChange={(e) => handleChange('capacity', parseInt(e.target.value, 10) || 12)}
                helperText="Seat limit. Default is 12."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Demo or trial</InputLabel>
                <Select
                  size="small"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  label="Demo or trial"
                >
                  <MenuItem value="demo">Demo</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Demo join link"
                placeholder="https://zoom.us/j/…"
                value={formData.meeting_url}
                onChange={(e) => handleChange('meeting_url', e.target.value)}
                required
                error={!meetingOk}
                helperText="Required. Saved with slot settings."
              />
            </Grid>
            {formData.teacher_id && formData.scheduled_date ? (
              <Grid item xs={12}>
                {loadingSlots ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Loading this teacher&apos;s hours…</Typography>
                  </Box>
                ) : availableTimeRanges.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Free gaps for {teacherInfo?.name}:</Typography>
                    {availableTimeRanges.map((range, index) => (
                      <Chip
                        key={index}
                        size="small"
                        variant="outlined"
                        label={`${formatTimeAMPM(range.start_time)} – ${formatTimeAMPM(range.end_time)}`}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No free gaps listed for this day. You can still save the time.
                  </Typography>
                )}
                <DemoScheduleExceptionPanel
                  reason={scheduleExceptionReason}
                  allowOverride={allowScheduleException}
                  onAllowOverrideChange={setAllowScheduleException}
                  disabled={busy}
                />
              </Grid>
            ) : null}
          </Grid>
        </FormSection>

        <Grid item xs={12}><Divider /></Grid>

        <FormSection
          title="4. Name and notes"
          hint="Name saves with slot settings. Notes for a moved slot save only with Save new time."
        >
          <TextField
            fullWidth
            size="small"
            label="Demo name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Reason for a new time (optional)"
            multiline
            rows={2}
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            sx={{ mt: 1.5 }}
          />
        </FormSection>
      </Grid>

      {settingsDirty && scheduleDirty ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          You changed both the clock and slot settings. Use each button for its own changes — one save will not apply the other.
        </Alert>
      ) : null}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
        <Button onClick={onCancel} disabled={busy}>
          Close
        </Button>
        <Button
          onClick={handleSaveSettings}
          variant="outlined"
          disabled={
            busy ||
            !settingsDirty ||
            !formData.countries?.length ||
            !meetingOk ||
            (landingChannel && !formData.assigned_sales_user_id)
          }
        >
          {saving === 'settings' ? 'Saving…' : 'Save settings'}
        </Button>
        <Button
          onClick={handleSaveNewTime}
          variant="contained"
          disabled={
            busy ||
            !scheduleDirty ||
            !formData.scheduled_date ||
            !formData.scheduled_time ||
            !meetingOk ||
            !!(formData.teacher_id && scheduleExceptionReason && !allowScheduleException)
          }
        >
          {saving === 'time' ? 'Saving…' : 'Save new time'}
        </Button>
      </Box>
    </Box>
  );
};

export default DemoEditForm;
